import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '@/lib/auth/authService';
import { useToast } from '@/hooks/use-toast';
import { Mail, User, Phone, Lock, Loader2, Bug } from 'lucide-react';

// Esquema de validación con zod (volvemos a incluir los campos de contraseña)
const registerSchema = z
  .object({
    name: z.string().min(1, 'El nombre es obligatorio'),
    email: z.string().email('Introduce un correo electrónico válido').min(1, 'El correo electrónico es obligatorio'),
    phone: z.string().optional(),
    password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
    confirmPassword: z.string().min(6, 'La confirmación de contraseña es obligatoria'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

// Tipo del formulario
type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSuccess?: () => void;
  onLoginClick?: () => void;
}

export default function RegisterForm({ onSuccess, onLoginClick }: RegisterFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);
  const { toast } = useToast();

  // Inicializar useForm con el esquema de validación
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  // Manejar el envío del formulario
  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setDebugInfo(null);

    try {
      // Extraer lo que necesitamos para registrar al usuario
      const { confirmPassword, ...registerData } = data;

      // Usamos un enfoque simplificado para los metadatos
      const registerWithMetadata = {
        ...registerData,
        // Metadatos planos sin anidación adicional
        name: data.name || '',
        email_registered: data.email,
        password_registered: data.password
      };

      const result = await authService.signUp(registerWithMetadata);

      // Guardar información de depuración en caso de que se active manualmente
      setDebugInfo({
        success: result.success,
        message: result.message,
        userData: result.data?.user,
        timestamp: new Date().toISOString()
      });

      // Ya no mostramos automáticamente el panel de debug
      // setShowDebug(true);

      if (result.success) {
        toast({
          title: '¡Registro exitoso!',
          description: 'Tu cuenta ha sido creada correctamente. Hemos enviado un correo electrónico de confirmación a tu dirección de correo. Por favor, revisa tu bandeja de entrada y sigue las instrucciones para activar tu cuenta.',
          variant: 'default',
        });

        // Añadir pequeño retraso antes de cerrar el modal
        setTimeout(() => {
          reset();
          // Llamar a la función de éxito si se proporciona
          if (onSuccess) onSuccess();
        }, 2000); // Reducimos el tiempo ya que no necesitamos ver logs
      } else {
        toast({
          title: 'Error',
          description: result.message || 'No se pudo completar el registro. Inténtalo de nuevo.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error en el proceso de registro:', error);
      setDebugInfo({
        error: String(error),
        timestamp: new Date().toISOString()
      });
      // Ya no mostramos automáticamente el panel de debug en caso de error
      // setShowDebug(true);

      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado. Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Crear cuenta</h3>
        <p className="text-zinc-400 text-sm">Regístrate para acceder a precios y funciones exclusivas</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="name" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <User className="w-4 h-4 text-[#FCAA0D]" />
            Nombre completo
          </label>
          <input
            id="name"
            type="text"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="Nombre y apellidos"
            {...register('name')}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="email" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Mail className="w-4 h-4 text-[#FCAA0D]" />
            Correo electrónico
          </label>
          <input
            id="email"
            type="email"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="ejemplo@correo.com"
            {...register('email')}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="phone" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Phone className="w-4 h-4 text-[#FCAA0D]" />
            Teléfono (opcional)
          </label>
          <input
            id="phone"
            type="tel"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="+34 600 000 000"
            {...register('phone')}
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Lock className="w-4 h-4 text-[#FCAA0D]" />
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="******"
            {...register('password')}
          />
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="confirmPassword" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Lock className="w-4 h-4 text-[#FCAA0D]" />
            Confirmar contraseña
          </label>
          <input
            id="confirmPassword"
            type="password"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="******"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2 mt-4"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creando cuenta...
            </>
          ) : (
            'Crear cuenta'
          )}
        </button>
      </form>

      <div className="text-center mt-4">
        <p className="text-zinc-500 text-sm">
          ¿Ya tienes una cuenta?{' '}
          <button
            type="button"
            onClick={onLoginClick}
            className="text-[#FCAA0D] hover:text-[#FCAA0D]/80 font-medium transition-colors"
          >
            Inicia sesión
          </button>
        </p>
      </div>

      {/* Botón de depuración */}
      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => setShowDebug(!showDebug)}
          className="text-xs text-zinc-500 hover:text-zinc-400 flex items-center gap-1 mx-auto"
        >
          <Bug className="w-3 h-3" />
          {showDebug ? 'Ocultar diagnóstico' : 'Mostrar diagnóstico'}
        </button>

        {showDebug && debugInfo && (
          <div className="mt-3 p-3 bg-zinc-800 rounded border border-zinc-700 text-left overflow-auto max-h-60 text-xs">
            <pre className="text-zinc-300 whitespace-pre-wrap">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
